/**
 * 🖨️ Print Receipt Utility
 *
 * Generates standardized, professional print receipts for all booking types.
 * This ensures consistent receipt layout across the entire application.
 *
 * Updated to work with V2 API flat structure (BookingV2) - no transformations needed.
 */

import { formatCurrency } from './formatCurrency';
import type { BookingV2 } from '@/types/booking';

export interface PrintableBooking {
  // V2 flat structure fields
  id?: string;
  bookingNumber: string;
  bookingType: string;
  serviceType?: string;
  status: string;
  paymentStatus: string;

  // Dates
  startDate: string;
  endDate: string;

  // Amounts (V2 returns as strings)
  totalAmount: string | number;
  dailyRate?: string | number;
  securityDeposit?: string | number;
  securityDepositAmount?: string | number | null;

  // User fields (flat)
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  user_phone: string;

  // Vehicle fields (flat)
  vehicle_name: string;

  // Locations
  pickupLocation: string;
  dropoffLocation?: string;

  // Payment
  paymentMethod?: string;

  // Legacy/Optional fields for backward compatibility
  bookingRef?: string;
  type?: string;
  customer?: {
    name: string;
    email: string;
    phone: string;
  };
  vehicle?: {
    make: string;
    model: string;
    year: number;
    plate?: string;
  };
  duration?: string;
  timezone?: string;
  serviceLocation?: string;
  location?: string;
  amount?: number;
  notes?: string;
  pricingBreakdown?: any;
  pricing?: any;
  payment?: any;
}

/**
 * Helper to get booking type label
 */
function getBookingTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    'rental': 'Car Rental',
    'airport-transfer': 'Airport Transfer',
    'yacht-charter': 'Yacht Charter',
    'driver-hire': 'Driver Hire',
    'vehicle-rental': 'Vehicle Rental',
    'with-driver': 'With Driver',
    'self-drive': 'Self Drive',
  };
  return labels[type] || type;
}

/**
 * Helper to get timezone label
 */
function getTimezoneLabel(timezone?: string, location?: string): string {
  if (!timezone) return 'N/A';

  const timezones: Record<string, string> = {
    'Africa/Lagos': 'West Africa Time (WAT)',
    'Africa/Abuja': 'West Africa Time (WAT)',
  };

  return timezones[timezone] || timezone;
}

/**
 * Format date in service timezone
 */
function formatDateInServiceTimezone(
  dateStr: string,
  timezone?: string,
  options?: Intl.DateTimeFormatOptions
): string {
  try {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr || 'Invalid Date';
    return date.toLocaleDateString('en-US', options || { dateStyle: 'medium' });
  } catch {
    return dateStr || 'N/A';
  }
}

/**
 * Generate HTML for printable receipt
 */
export function generateReceiptHTML(booking: PrintableBooking): string {
  const pricingBreakdown = booking.pricingBreakdown || booking.pricing || {};
  const payment = booking.payment || {};

  // Handle both V2 flat (list) and V2 nested (single) structures
  const bookingRef = booking.bookingNumber || booking.bookingRef || booking.bookingReference || 'N/A';

  // Customer: flat (user_first_name) or nested (customer.name)
  const customerName = booking.user_first_name && booking.user_last_name
    ? `${booking.user_first_name} ${booking.user_last_name}`
    : booking.customer?.name || 'N/A';
  const customerEmail = booking.user_email || booking.customer?.email || 'N/A';
  const customerPhone = booking.user_phone || booking.customer?.phone || 'N/A';

  // Vehicle: flat (vehicle_name) or nested (vehicle.make + model)
  const vehicleDisplay = booking.vehicle_name ||
    (booking.vehicle ? `${booking.vehicle.make} ${booking.vehicle.model} (${booking.vehicle.year})` : 'N/A');

  // Service type: flat or nested in booking object
  const serviceTypeValue = booking.serviceType || (booking as any).booking?.type || booking.type || 'N/A';

  // Handle location - might be string or object
  const getLocationString = (loc: any): string => {
    if (!loc) return 'N/A';
    if (typeof loc === 'string') return loc;
    if (typeof loc === 'object') {
      return loc.address || loc.name || loc.pickup || JSON.stringify(loc);
    }
    return String(loc);
  };

  // Location: flat (pickupLocation) or nested (location.pickup)
  const pickupLoc = booking.pickupLocation || (booking as any).location?.pickup || booking.serviceLocation || booking.location;
  const dropoffLoc = booking.dropoffLocation || (booking as any).location?.dropoff;
  const serviceLocation = getLocationString(pickupLoc);

  // Dates: flat (startDate) or nested (booking.startDate)
  const startDate = booking.startDate || (booking as any).booking?.startDate;
  const endDate = booking.endDate || (booking as any).booking?.endDate;

  // Calculate duration from dates if not provided
  const calculateDuration = () => {
    if (booking.duration) return booking.duration;
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      // Check if dates are valid
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return 'N/A';
      }
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
    }
    return 'N/A';
  };
  const durationDisplay = calculateDuration();

  // Extract costs - V2 now includes flat fields + costBreakdown object
  const costBreakdown = booking.costBreakdown || pricingBreakdown || {};

  // Amount: V2 flat (totalAmount) or nested (payment.amount)
  const totalAmountRaw = booking.totalAmount || (booking as any).payment?.amount || booking.amount || 0;
  const totalAmountValue = typeof totalAmountRaw === 'string' ? parseFloat(totalAmountRaw) : totalAmountRaw;

  // V2 flat fields (prioritize these) or fallback to nested costBreakdown or legacy pricingBreakdown
  const rentalCost = booking.rentalCost || costBreakdown.rentalCost || costBreakdown.basePrice || 0;
  const bookingFeeRaw = booking.bookingFee || costBreakdown.bookingFee || 0;
  const bookingFee = typeof bookingFeeRaw === 'string' ? parseFloat(bookingFeeRaw) : bookingFeeRaw;
  const bookingFeeRate = booking.bookingFeeRate || costBreakdown.bookingFeeRate || 0;
  const vat = booking.vatAmount || costBreakdown.vat || 0;
  const vatRate = booking.vatRate || costBreakdown.vatRate || 0;

  // Security deposit: V2 flat or nested
  const securityDepositRaw = booking.securityDeposit || (booking as any).payment?.securityDeposit || costBreakdown.securityDeposit || 0;
  const securityDepositValue = typeof securityDepositRaw === 'string' ? parseFloat(securityDepositRaw) : securityDepositRaw;

  const serviceFee = booking.serviceFee || costBreakdown.serviceFee || 0;
  const serviceFeeRate = booking.serviceFeeRate || costBreakdown.serviceFeeRate || 0;
  const hostPayout = costBreakdown.hostPayout || 0;

  // Payment status: root level in both structures
  const paymentStatus = booking.paymentStatus || 'pending';
  const bookingStatus = booking.status || 'pending';

  // Booking type/mode
  const bookingTypeMode = booking.bookingType || 'request';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Receipt - ${booking.bookingRef}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          padding: 40px;
          max-width: 800px;
          margin: 0 auto;
          color: #333;
        }
        .header {
          text-align: center;
          margin-bottom: 40px;
          padding-bottom: 20px;
          border-bottom: 3px solid #1976d2;
        }
        .company-name {
          font-size: 32px;
          font-weight: 700;
          color: #1976d2;
          margin-bottom: 8px;
        }
        .receipt-title {
          font-size: 20px;
          color: #666;
          margin-bottom: 8px;
        }
        .booking-ref {
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }
        .invoice-meta {
          display: flex;
          justify-content: space-between;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #e0e0e0;
        }
        .invoice-meta-item {
          text-align: left;
        }
        .invoice-meta-label {
          font-size: 12px;
          color: #999;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .invoice-meta-value {
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }
        .section {
          margin-bottom: 30px;
        }
        .section-title {
          font-size: 16px;
          font-weight: 600;
          color: #1976d2;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 2px solid #e0e0e0;
        }
        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        .info-label {
          color: #666;
          font-weight: 500;
        }
        .info-value {
          font-weight: 600;
          text-align: right;
        }
        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .status-paid {
          background-color: #d4edda;
          color: #155724;
        }
        .status-pending {
          background-color: #fff3cd;
          color: #856404;
        }
        .status-cancelled {
          background-color: #f8d7da;
          color: #721c24;
        }
        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 2px solid #e0e0e0;
          text-align: center;
          color: #666;
          font-size: 14px;
        }
        @media print {
          body { padding: 20px; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-name">PolarRide</div>
        <div class="receipt-title">Tax Invoice / Receipt</div>
        <div class="booking-ref">Booking Reference: ${bookingRef}</div>
        <div class="invoice-meta">
          <div class="invoice-meta-item">
            <div class="invoice-meta-label">Invoice Number</div>
            <div class="invoice-meta-value">INV-${bookingRef}</div>
          </div>
          <div class="invoice-meta-item">
            <div class="invoice-meta-label">Invoice Date</div>
            <div class="invoice-meta-value">${new Date().toLocaleDateString('en-US', { dateStyle: 'medium' })}</div>
          </div>
          <div class="invoice-meta-item">
            <div class="invoice-meta-label">Payment Status</div>
            <div class="invoice-meta-value">
              <span class="status-badge ${paymentStatus === 'paid' ? 'status-paid' : 'status-pending'}">
                ${paymentStatus.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Booking Information</div>
        <div class="info-row">
          <span class="info-label">Service Type:</span>
          <span class="info-value">${getBookingTypeLabel(serviceTypeValue)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Booking Mode:</span>
          <span class="info-value">${bookingTypeMode === 'instant' ? 'Instant Booking' : 'Approval Required'}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Status:</span>
          <span class="info-value">
            <span class="status-badge ${
              bookingStatus === 'active' || bookingStatus === 'confirmed' ? 'status-paid' :
              bookingStatus === 'cancelled' ? 'status-cancelled' : 'status-pending'
            }">${bookingStatus.toUpperCase()}</span>
          </span>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Customer Details</div>
        <div class="info-row">
          <span class="info-label">Name:</span>
          <span class="info-value">${customerName}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Email:</span>
          <span class="info-value">${customerEmail}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Phone:</span>
          <span class="info-value">${customerPhone}</span>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Service Details</div>
        ${booking.vehicle_name || booking.vehicle ? `
          <div class="info-row">
            <span class="info-label">Vehicle:</span>
            <span class="info-value">${vehicleDisplay}</span>
          </div>
          ${booking.vehicle?.plate && booking.vehicle.plate !== 'N/A' ? `
          <div class="info-row">
            <span class="info-label">Plate Number:</span>
            <span class="info-value">${booking.vehicle.plate}</span>
          </div>` : ''}
        ` : ''}
        ${booking.yacht ? `
          <div class="info-row">
            <span class="info-label">Yacht:</span>
            <span class="info-value">${booking.yacht.name}${booking.yacht.model ? ` (${booking.yacht.model})` : ''}</span>
          </div>
        ` : ''}
        ${booking.driver ? `
          <div class="info-row">
            <span class="info-label">Driver:</span>
            <span class="info-value">${booking.driver.name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Driver Phone:</span>
            <span class="info-value">${booking.driver.phone}</span>
          </div>
        ` : ''}
        <div class="info-row">
          <span class="info-label">Duration:</span>
          <span class="info-value">${durationDisplay}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Start Date:</span>
          <span class="info-value">${formatDateInServiceTimezone(startDate, booking.timezone, { dateStyle: 'full' })}</span>
        </div>
        ${endDate ? `
          <div class="info-row">
            <span class="info-label">End Date:</span>
            <span class="info-value">${formatDateInServiceTimezone(endDate, booking.timezone, { dateStyle: 'full' })}</span>
          </div>
        ` : ''}
        ${booking.timezone ? `
          <div class="info-row">
            <span class="info-label">Timezone:</span>
            <span class="info-value">${getTimezoneLabel(booking.timezone, booking.serviceLocation)}</span>
          </div>
        ` : ''}
        <div class="info-row">
          <span class="info-label">Pickup Location:</span>
          <span class="info-value">${serviceLocation}</span>
        </div>
        ${dropoffLoc ? `
        <div class="info-row">
          <span class="info-label">Dropoff Location:</span>
          <span class="info-value">${getLocationString(dropoffLoc)}</span>
        </div>
        ` : ''}
      </div>

      <div class="section">
        <div class="section-title">Cost Breakdown</div>
        <div style="margin-bottom: 12px; font-weight: 600; color: #666; font-size: 14px;">Customer Pays:</div>
        <div class="info-row">
          <span class="info-label">Rental Cost</span>
          <span class="info-value">${formatCurrency(rentalCost)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Booking Fee (${bookingFeeRate}%)</span>
          <span class="info-value">${formatCurrency(bookingFee)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">VAT (${vatRate}%)</span>
          <span class="info-value">${formatCurrency(vat)}</span>
        </div>
        ${securityDepositValue > 0 ? `
        <div class="info-row">
          <span class="info-label" style="color: #ff9800;">Security Deposit (Refundable)</span>
          <span class="info-value" style="color: #ff9800;">${formatCurrency(securityDepositValue)}</span>
        </div>
        ` : ''}

        <div style="margin: 16px 0; padding: 16px; background-color: #e3f2fd; border-radius: 4px;">
          <div class="info-row" style="border: none; margin: 0;">
            <span style="font-weight: 700; font-size: 18px;">Total Amount Paid</span>
            <span style="font-weight: 700; font-size: 18px; color: #1976d2;">${formatCurrency(totalAmountValue)}</span>
          </div>
        </div>
      </div>

      <div style="margin-top: 24px; padding: 16px; background-color: #f5f5f5; border-radius: 4px; text-align: center;">
        <div style="font-size: 12px; color: #666; margin-bottom: 8px;">Payment Status</div>
        <span class="status-badge ${paymentStatus === 'paid' ? 'status-paid' : 'status-pending'}">
          ${paymentStatus.toUpperCase()}
        </span>
      </div>

      ${booking.notes && typeof booking.notes === 'string' ? `
      <div class="section">
        <div class="section-title">Additional Notes</div>
        <p style="padding: 12px; background-color: #f8f9fa; border-radius: 4px; line-height: 1.6;">
          ${booking.notes}
        </p>
      </div>
      ` : ''}

      <div class="footer">
        <p>Thank you for choosing PolarRide!</p>
        <p style="margin-top: 8px;">For support, contact us at support@polarride.com</p>
        <p style="margin-top: 16px; font-size: 12px; color: #999;">
          Generated on ${new Date().toLocaleDateString('en-US', { dateStyle: 'full' })}
        </p>
      </div>
    </body>
    </html>
  `;
}

/**
 * Print a receipt by opening it in a new window
 */
export function printReceipt(booking: PrintableBooking): void {
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    console.error('Unable to open print window. Please allow popups.');
    return;
  }

  const receiptHTML = generateReceiptHTML(booking);
  printWindow.document.write(receiptHTML);
  printWindow.document.close();

  // Wait for content to load then print
  printWindow.onload = () => {
    printWindow.print();
  };
}
